/**
 * Графики разделов «Продажи» и «Склад», которых нет в финансовом блоке.
 *
 * Те же правила: HTML, а не SVG; меняется только transform (scaleX/scaleY
 * от оси, translate для меток), поэтому рост при первом показе и перетекание
 * при смене фильтра делает CSS-переход. Значения подписаны текстом.
 *
 *   StackRows    — сравнение по категориям со структурой внутри
 *                  (склад: сумма запаса, разбитая по возрасту)
 *   MiniColumns  — малая динамика за 12 месяцев в строке списка
 *                  (несколько клиентов рядом читаются лучше пяти линий в одной сетке)
 */

import { type CSSProperties, type ReactNode } from 'react'
import { SegmentBar, TweenText, type Segment } from '../finance/charts'
import { useEntered } from '../finance/motion'
import { formatMoneyShort, formatPercent } from '../format'

// --- Полосы со структурой ---------------------------------------------------

export interface StackRow {
  key: string
  label: string
  sub?: ReactNode
  segments: Segment[]
}

export function StackRows({
  rows,
  ariaLabel,
  onSelect,
  format = formatMoneyShort,
}: {
  rows: StackRow[]
  ariaLabel: string
  onSelect?: (row: StackRow, element: HTMLElement) => void
  format?: (value: number) => string
}) {
  const entered = useEntered()
  const totalOf = (row: StackRow) => row.segments.reduce((sum, item) => sum + Math.max(0, item.value), 0)
  const max = Math.max(1, ...rows.map(totalOf))

  return (
    <div className={`stack-rows${onSelect ? ' stack-rows--interactive' : ''}`} role="list" aria-label={ariaLabel}>
      {rows.map((row, index) => {
        const total = totalOf(row)
        const content = (
          <>
            <span className="bars__label">
              <span className="bars__name">{row.label}</span>
              {row.sub && <span className="bars__sub">{row.sub}</span>}
            </span>
            <span className="stack-rows__track">
              {/* Полоса целиком масштабируется к своей сумме, сегменты внутри —
                  к своим долям: длина сравнивает склады, цвет — возраст. */}
              <span className="stack-rows__scale" style={{ transform: `scaleX(${entered ? total / max : 0})` }}>
                <SegmentBar segments={row.segments} ariaLabel={`${row.label}: структура`} />
              </span>
            </span>
            <span className="bars__values">
              <span className="bars__value mono">
                <TweenText value={total} format={format} />
              </span>
            </span>
          </>
        )
        const style = { '--i': Math.min(index, 7) } as CSSProperties
        return onSelect ? (
          <button
            key={row.key}
            type="button"
            role="listitem"
            className="stack-rows__row cascade"
            style={style}
            onClick={(event) => onSelect(row, event.currentTarget)}
            aria-label={`${row.label}: подробнее`}
          >
            {content}
          </button>
        ) : (
          <div key={row.key} role="listitem" className="stack-rows__row cascade" style={style}>
            {content}
          </div>
        )
      })}
    </div>
  )
}

// --- Малая динамика ----------------------------------------------------------

export function MiniColumns({
  values,
  missing,
  labels,
  max,
  ariaLabel,
  color = 'var(--series-1)',
}: {
  values: Array<number | null>
  /** Месяц не загружен или закрыт правами — пунктир, а не «нет продаж». */
  missing?: Array<'nodata' | 'closed' | undefined>
  labels: string[]
  /** Общая шкала для нескольких строк: иначе маленький клиент выглядит как крупный. */
  max?: number
  ariaLabel: string
  color?: string
}) {
  const entered = useEntered()
  const scaleMax = max ?? Math.max(1, ...values.map((value) => Math.abs(value ?? 0)))
  return (
    <span className="mini-columns" role="img" aria-label={ariaLabel}>
      {values.map((value, index) => {
        const gap = missing?.[index]
        return (
        <span
          key={index}
          className={`mini-columns__slot${gap ? ' mini-columns__slot--missing' : ''}`}
          title={`${labels[index] ?? ''}: ${gap === 'closed' ? 'закрыто правами' : gap ? 'нет данных' : value === null ? 'нет продаж' : formatMoneyShort(value)}`}
        >
          <span
            className={`mini-columns__bar${value === null || value === 0 ? ' mini-columns__bar--zero' : ''}`}
            style={{
              transform: `scaleY(${entered && !gap ? Math.max(0, Math.min(1, (value ?? 0) / scaleMax)) : 0})`,
              background: color,
              transitionDelay: entered ? undefined : `calc(${Math.min(index, 7)} * var(--stagger))`,
            }}
          />
        </span>
        )
      })}
    </span>
  )
}

// --- Легенда со значениями ----------------------------------------------------

export function ValueLegend({
  items,
  total,
  format = formatMoneyShort,
}: {
  items: Array<{ key: string; label: string; color: string; value: number; note?: ReactNode }>
  total: number
  format?: (value: number) => string
}) {
  return (
    <div className="buckets insights-buckets" style={{ '--count': items.length } as CSSProperties}>
      {items.map((item, index) => (
        <div key={item.key} className="bucket cascade" style={{ '--i': index } as CSSProperties}>
          <div className="row row--tight" style={{ flexWrap: 'nowrap' }}>
            <span className="chart-legend__swatch" style={{ background: item.color }} aria-hidden="true" />
            <span className="small secondary">{item.label}</span>
          </div>
          <div className="bucket__value mono">
            <TweenText value={item.value} format={(value) => `${format(value)} ₽`} />
          </div>
          <div className="tiny muted">
            {total > 0 ? formatPercent((item.value / total) * 100) : '—'}
            {item.note ? <> · {item.note}</> : null}
          </div>
        </div>
      ))}
    </div>
  )
}
